import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import Badge from './Badge';
import {
  Copy, Check, ArrowRight, ArrowUpRight, CheckCircle2,
  Users, MessageSquare, Gift,
  Eye, BarChart3, Zap, GitBranch, Radio, Sparkles, TrendingUp, Award, Tag,
} from 'lucide-react';
import { getMaturityBand, getCurrencySymbol } from './utils';
import {
  SECTION_COLOURS, SECTION_NAMES_FULL, SECTION_QUESTIONS,
  SECTION_MAX_UPLIFT, SECTION_DESCRIPTIONS,
  GRADE_CONFIG, getDiagnosis,
} from './resultsData';
import QUESTIONS from './questionsData';
import QC from './contentData';

const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value, country) => {
  const sym = getCurrencySymbol(country);
  if (country === 'India') {
    if (value >= 10000000) return `${sym}${Math.round(value / 10000000)} Cr`;
    if (value >= 100000) return `${sym}${Math.round(value / 100000)} L`;
    return `${sym}${Math.round(value).toLocaleString('en-IN')}`;
  }
  return `${sym}${Math.round(value).toLocaleString('en-US')}`;
};

const BAND_COLOURS = {
  Foundational: '#ef4444', Developing: '#f59e0b', Capable: '#3b82f6',
  Advanced: '#22c55e', 'Best-in-class': '#7c3aed',
};

const SECTION_ICONS = [Users, MessageSquare, Gift];
const Q_ICONS = { 0: Eye, 1: BarChart3, 2: Zap, 3: GitBranch, 4: Radio, 5: Sparkles, 6: TrendingUp, 7: Award, 8: Tag };

const MATURITY_BADGE_STYLE = {
  A: { bg: '#FFF1F1', color: '#D94F4F' },
  B: { bg: '#FFF1F1', color: '#D94F4F' },
  C: { bg: '#FFF4EC', color: '#E07B2A' },
  D: { bg: '#EDFDF5', color: '#27AE60' },
};

/* ── Score Ring SVG ── */
const ScoreRing = ({ score, max, size, strokeWidth, color }) => {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / max);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E8F0FF" strokeWidth={strokeWidth} />
      <motion.circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeLinecap="round"
        initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
        transition={{ duration: 0.8, delay: 0.2 }} />
    </svg>
  );
};

/* ── Overall Score Gauge (270° arc) ── */
const ScoreGauge = ({ score, label, color }) => {
  const size = 220; const sw = 14; const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r; const arc = circ * 0.75;
  const filled = arc * (score / 100);
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-[135deg]">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={sw} strokeDasharray={`${arc} ${circ}`} strokeLinecap="round" />
          <motion.circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={sw} strokeDasharray={`${arc} ${circ}`} strokeLinecap="round"
            initial={{ strokeDashoffset: arc }} animate={{ strokeDashoffset: arc - filled }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.25,0.1,0.25,1] }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, delay: 0.5, type: 'spring' }}
            className="text-6xl font-bold" style={{ color }}>{score}</motion.span>
          <span className="text-lg font-medium text-[#94a3b8] -mt-1">/ 100</span>
        </div>
      </div>
      {label && <p className="text-sm font-medium text-[#64748b] mt-2">{label}</p>}
    </div>
  );
};

/* ══════════════════════════════════════
   CAPABILITY CARD — gap→solution layout
   ══════════════════════════════════════ */
const CapabilityCard = ({ qIdx, answers }) => {
  const q = QUESTIONS[qIdx];
  const ans = answers[qIdx];
  const letter = ans?.letter || 'A';
  const score = ans?.score || 0;
  const content = QC[qIdx]?.[letter];
  const grade = GRADE_CONFIG[letter];
  const badgeStyle = MATURITY_BADGE_STYLE[letter];
  const isD = letter === 'D';

  const ringColor = isD ? '#27AE60' : score <= 3 ? '#D94F4F' : score <= 7 ? '#E07B2A' : '#1F73FF';

  return (
    <div style={{ background: '#fff', borderRadius: 20, marginBottom: 16, boxShadow: '0 2px 16px rgba(31,115,255,0.07)', overflow: 'hidden' }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px 16px', borderBottom: '1px solid #EEF2FF' }}>
        <div className="relative flex-shrink-0" style={{ width: 52, height: 52 }}>
          <ScoreRing score={score} max={10} size={52} strokeWidth={5} color={ringColor} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span style={{ fontSize: 16, fontWeight: 700, color: ringColor }}>{score}</span>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#95A3B8', marginBottom: 3 }}>{q.tag}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#212121' }}>{q.cardTitle}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 100, background: badgeStyle.bg, color: badgeStyle.color, whiteSpace: 'nowrap' }}>
          {grade.label}
        </span>
      </div>

      {/* Answer D: success message */}
      {isD && content?.success && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '20px 24px', background: '#F0FDF4' }}>
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#27AE60' }} />
          <p style={{ fontSize: 13, color: '#166534', lineHeight: 1.6 }}>{content.success}</p>
        </div>
      )}

      {/* Non-D: gap → solution */}
      {!isD && content && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_52px_1fr]" style={{ minHeight: 110 }}>
            {/* Problem pane */}
            <div style={{ padding: '20px 24px', background: '#FFFAF6' }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#E07B2A', marginBottom: 8 }}>Current gap</div>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#212121', marginBottom: 6, lineHeight: 1.35 }}>{content.gapTitle}</h3>
              <p style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.55 }}>{content.gap}</p>
            </div>
            {/* Arrow connector */}
            <div className="hidden sm:flex items-center justify-center" style={{ background: '#fff', position: 'relative' }}>
              <div style={{ width: 36, height: 36, background: '#1F73FF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(31,115,255,0.3)', zIndex: 1 }}>
                <ArrowRight className="w-4 h-4 text-white" />
              </div>
            </div>
            {/* Solution pane */}
            <div style={{ padding: '20px 24px', background: '#F3F7FF' }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#1F73FF', marginBottom: 8 }}>Xeno solution</div>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#212121', marginBottom: 6, lineHeight: 1.35 }}>{content.solTitle}</h3>
              <p style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.55 }}>{content.sol}</p>
            </div>
          </div>
          {/* Impact footer */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 24px', background: '#F8FAFF', borderTop: '1px solid #EEF2FF' }}>
            <div style={{ width: 7, height: 7, background: '#1F73FF', borderRadius: '50%', flexShrink: 0, marginTop: 6 }} />
            <p style={{ fontSize: 12, color: '#595959', lineHeight: 1.5 }}>
              {content.value}<strong style={{ color: '#1F73FF', fontWeight: 600 }}>{content.bold}</strong>{content.valueSuffix || ''}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════
   RESULTS SCREEN
   ══════════════════════════════════════ */
const ResultsScreen = ({ answers, scores, inputData, gateData, onRestart, resultId }) => {
  const [copied, setCopied] = useState(false);
  const companyName = gateData?.companyName || 'Your Company';
  const finalScore = scores?.final || 0;
  const sectionScores = useMemo(() => scores?.sections || [0, 0, 0], [scores]);
  const maturityBand = getMaturityBand(finalScore);
  const bandColour = BAND_COLOURS[maturityBand];

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const revenueData = useMemo(() => {
    const mo = inputData.monthlyOrders || 0;
    const aov = inputData.aov || 0;
    const dcp = inputData.dataCollectionPct || 60;
    const rsp = inputData.repeatSalesPct || 40;
    const totalRevenue = mo * aov * 12;
    const mapRevenue = totalRevenue * (dcp / 100);
    const repeatRevenue = totalRevenue * (rsp / 100);
    const monthlyBase = mo * aov * (dcp / 100) * (rsp / 100);

    const areas = SECTION_NAMES_FULL.map((name, idx) => {
      const maxUplift = SECTION_MAX_UPLIFT[idx];
      const scorePct = sectionScores[idx] * 10;
      const upliftApplied = maxUplift * (100 - scorePct) / 100;
      const monthlyImpact = monthlyBase * (upliftApplied / 100);
      const annualImpact = monthlyImpact * 12;
      return { name, maxUplift, sectionScore: sectionScores[idx], upliftApplied, monthlyImpact, annualImpact, colorIdx: idx };
    });

    const totalAnnual = areas.reduce((s, a) => s + a.annualImpact, 0);
    const totalMonthly = areas.reduce((s, a) => s + a.monthlyImpact, 0);
    const repeatUpliftPct = repeatRevenue > 0 ? Math.round((totalAnnual / repeatRevenue) * 100) : 0;
    return { totalRevenue, mapRevenue, repeatRevenue, areas, totalMonthly, totalAnnual, repeatUpliftPct };
  }, [inputData, sectionScores]);

  // Collect non-D improvements for revenue section
  const improvements = useMemo(() => {
    const items = [];
    const allQs = [0,1,2,3,4,5,6,7,8];
    allQs.forEach(qIdx => {
      const ans = answers[qIdx];
      if (ans && ans.letter !== 'D') {
        const content = QC[qIdx]?.[ans.letter];
        if (content) {
          items.push({ qIdx, solTitle: content.solTitle, bold: content.bold, value: content.value, valueSuffix: content.valueSuffix || '' });
        }
      }
    });
    return items;
  }, [answers]);

  const shareUrl = resultId ? `${FRONTEND_URL}/roi-calculator/results/${resultId}` : window.location.href;
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const glassCard = {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
  };

  return (
    <div className="w-full pb-20" data-testid="results-screen">
      {/* ── HEADER ── */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-8">
        <p className="text-xs font-semibold text-[#3b82f6] tracking-wide uppercase">CRM & Loyalty Maturity Audit — Results</p>
        <h1 className="text-[28px] font-bold text-[#0f172a] mt-1 mb-1.5">{companyName}'s Maturity Report</h1>
        <p className="text-sm text-[#64748b]">Based on your answers across 9 questions and 3 capability areas.</p>
      </motion.div>

      {/* ══════ OVERALL SCORE ══════ */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }} className="rounded-2xl p-6 sm:p-8 mb-5" style={glassCard}>
        <div className="flex flex-col sm:flex-row items-center gap-8 sm:gap-10">
          <div className="flex-shrink-0">
            <ScoreGauge score={finalScore} label={companyName} color={bandColour} />
            <div className="flex justify-center mt-3"><Badge level={maturityBand} /></div>
          </div>
          <div className="flex-1 w-full">
            <p className="text-sm font-semibold text-[#334155] mb-5 uppercase tracking-wide">Score by capability</p>
            <div className="flex flex-col gap-4">
              {sectionScores.map((score, idx) => {
                const Icon = SECTION_ICONS[idx];
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: SECTION_COLOURS[idx] }} />
                        <span className="text-[13px] font-semibold text-[#334155] uppercase tracking-wide">{SECTION_NAMES_FULL[idx]}</span>
                      </div>
                      <div>
                        <span className="text-lg font-bold" style={{ color: SECTION_COLOURS[idx] }}>{Math.round(score * 10)}</span>
                        <span className="text-sm font-medium text-[#94a3b8]">/100</span>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full overflow-hidden bg-[#e2e8f0]">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${score * 10}%` }} transition={{ duration: 0.8, delay: 0.4 + 0.15 * idx }} className="h-full rounded-full" style={{ backgroundColor: SECTION_COLOURS[idx] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Share Row ── */}
      <div className="flex justify-end gap-3 mb-12">
        <motion.button onClick={handleCopy} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-[#334155] rounded-full cursor-pointer"
          style={{ border: '1px solid #e2e8f0', background: 'rgba(255,255,255,0.8)' }} data-testid="copy-link-button">
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Link copied!' : 'Share results'}
        </motion.button>
      </div>

      {/* ══════ SECTION PERFORMANCE ══════ */}
      {[0, 1, 2].map(sIdx => {
        const Icon = SECTION_ICONS[sIdx];
        const score = sectionScores[sIdx];
        const scorePct = Math.round(score * 10);
        const qIndices = SECTION_QUESTIONS[sIdx] || [];
        const nonDCount = qIndices.filter(qi => answers[qi] && answers[qi].letter !== 'D').length;

        return (
          <motion.div key={sIdx} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + sIdx * 0.12 }} className="mb-16">
            {/* Section divider */}
            {sIdx > 0 && (
              <div className="flex items-center gap-4 mb-8">
                <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#CBD5E1' }}>Section {sIdx + 1} of 3</span>
                <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
              </div>
            )}
            {/* Section header card */}
            <div style={{ background: '#fff', borderRadius: 20, padding: '28px 32px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 28, boxShadow: '0 2px 16px rgba(31,115,255,0.08)', borderLeft: `4px solid ${SECTION_COLOURS[sIdx]}` }}>
              <div className="relative flex-shrink-0" style={{ width: 88, height: 88 }}>
                <ScoreRing score={scorePct} max={100} size={88} strokeWidth={7} color={SECTION_COLOURS[sIdx]} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span style={{ fontSize: 22, fontWeight: 700, color: SECTION_COLOURS[sIdx] }}>{scorePct}</span>
                  <span style={{ fontSize: 11, color: '#95A3B8', fontWeight: 500 }}>/100</span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: SECTION_COLOURS[sIdx], textTransform: 'uppercase', marginBottom: 4 }}>CRM Audit · {SECTION_NAMES_FULL[sIdx]}</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#212121', marginBottom: 4 }}>{SECTION_NAMES_FULL[sIdx]}</h2>
                <p style={{ fontSize: 13, color: '#595959' }}>{SECTION_DESCRIPTIONS[sIdx]}</p>
              </div>
              {nonDCount > 0 && (
                <span style={{ background: '#E8F0FF', color: '#1F73FF', fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 100, whiteSpace: 'nowrap' }}>
                  {nonDCount} {nonDCount === 1 ? 'area' : 'areas'} to improve
                </span>
              )}
            </div>

            {/* Section label */}
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#95A3B8', textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>
              Capability breakdown
            </div>

            {/* Capability cards — connected with left border */}
            <div style={{ borderLeft: `2px solid ${SECTION_COLOURS[sIdx]}20`, marginLeft: 24, paddingLeft: 20 }}>
              {qIndices.map(qIdx => (
                <CapabilityCard key={qIdx} qIdx={qIdx} answers={answers} />
              ))}
            </div>
          </motion.div>
        );
      })}

      {/* ══════ REVENUE HERO ══════ */}
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#CBD5E1' }}>Revenue Impact</span>
        <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.7 }}>
        {/* Blue hero */}
        <div style={{ background: '#1F73FF', borderRadius: 24, padding: '40px 40px 36px', marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: -60, top: -60, width: 280, height: 280, borderRadius: '50%', border: '40px solid rgba(255,255,255,0.07)' }} />
          <div style={{ position: 'absolute', right: 40, top: 60, width: 160, height: 160, borderRadius: '50%', border: '28px solid rgba(255,255,255,0.05)' }} />

          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', marginBottom: 16, position: 'relative', zIndex: 1 }}>
            Revenue Opportunity · Estimated Incremental Uplift
          </div>
          <div className="flex flex-wrap items-end gap-5" style={{ position: 'relative', zIndex: 1, marginBottom: 8 }}>
            <span style={{ fontSize: 72, fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: -2 }}>
              {formatCurrency(revenueData.totalAnnual, inputData.country)}
            </span>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>per year</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>in incremental repeat revenue</div>
            </div>
            <div style={{ width: 1, height: 48, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{revenueData.repeatUpliftPct}%</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>repeat revenue uplift</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.12)', position: 'relative', zIndex: 1 }}>
            Based on <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{formatCurrency(revenueData.repeatRevenue, inputData.country)} repeat revenue</span> mapped from a <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{formatCurrency(revenueData.totalRevenue, inputData.country)}</span> revenue base — a <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{revenueData.repeatUpliftPct}% blended uplift</span> across all improvement areas.
          </div>
        </div>

        {/* What unlocks this revenue */}
        {improvements.length > 0 && (
          <>
            <div className="flex items-center gap-3 mb-3.5 px-1 mt-4">
              <span style={{ fontSize: 13, fontWeight: 700, color: '#212121', textTransform: 'uppercase', letterSpacing: '0.06em' }}>What unlocks this revenue</span>
              <div style={{ flex: 1, height: 1, background: '#D4E4FF' }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {improvements.map(({ qIdx, solTitle }) => {
                const QIcon = Q_ICONS[qIdx] || Eye;
                return (
                  <div key={qIdx} style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(31,115,255,0.06)', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: 4, background: '#1F73FF', flexShrink: 0 }} />
                    <div style={{ flex: 1, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, background: '#E8F0FF', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <QIcon className="w-[18px] h-[18px]" style={{ color: '#1F73FF' }} />
                      </div>
                      <h3 style={{ fontSize: 13, fontWeight: 700, color: '#212121', lineHeight: 1.3 }}>{solTitle}</h3>
                    </div>
                  </div>
                );
              })}
            </div>
            <p style={{ fontSize: 11, color: '#95A3B8', textAlign: 'center', marginTop: 20, lineHeight: 1.6 }}>
              Estimates are projections based on your CRM audit scores and revenue inputs.<br/>Actual results depend on implementation quality and customer base behaviour.
            </p>
          </>
        )}
      </motion.div>

      {/* ══════ CTA ══════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.8 }}
        className="mt-10 rounded-2xl text-left px-8 sm:px-10 py-10" style={glassCard}>
        <h2 className="text-[26px] font-bold text-[#0f172a] leading-tight max-w-[420px]">See what Xeno can unlock for you</h2>
        <p className="text-[15px] text-[#64748b] mt-4 max-w-[480px]">
          Your audit identified {formatCurrency(revenueData.totalAnnual, inputData.country)} in annual incremental revenue opportunity. Let's build a plan to capture it.
        </p>
        <a href="https://meetings-na2.hubspot.com/nandita-khanna/demo-xeno?utm_source=hs_email&utm_medium=email&_hsenc=p2ANqtz-_nwxDm32U_1Vs4kJdlNyxTDfxa6ZY7sS3YUYLXOktWbxKjcQTAbYBm3q9FW7dK4UzHs097&uuid=782c1ebe-dbd3-4c10-bb37-d61a3b59c1cb" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 bg-[#2563eb] text-white text-sm font-semibold rounded-full hover:bg-[#1d4ed8] hover:scale-[1.02] transition-all shadow-md cursor-pointer"
          data-testid="book-call-button">
          Book a call with the Xeno team
          <ArrowRight className="w-4 h-4" />
        </a>
        <p className="text-xs text-[#94a3b8] mt-4">No pitch. No pressure. Just a structured 30-minute session to walk through your results.</p>
      </motion.div>
    </div>
  );
};

export default ResultsScreen;
